// Declared-condition rules for the offer builder's outbound side, mirroring
// /sending exactly (EditTradeItemModal): a sender may declare any of the four
// conditions freely — no constraint relative to the have row — intersected
// with what the recipient's want record accepts. DAMAGED is never declarable,
// matching /sending's four-option list; a DAMAGED row can still be offered at
// its own condition when the recipient accepts DAMAGED.

export const DECLARABLE_CONDITIONS = [
  'NEAR_MINT',
  'SLIGHTLY_PLAYED',
  'MODERATELY_PLAYED',
  'HEAVILY_PLAYED',
] as const;

const conditionRank = (condition: string): number => {
  const i = DECLARABLE_CONDITIONS.indexOf(condition as (typeof DECLARABLE_CONDITIONS)[number]);
  return i === -1 ? DECLARABLE_CONDITIONS.length : i; // DAMAGED ranks worst
};

/**
 * The conditions this row may be offered at: declarable ∩ accepted, plus the
 * row's own condition when the recipient accepts it (covers DAMAGED rows).
 * `accepted` is the backend's explicit effective list — an accepts-any want
 * lists all four; absent (old payloads) means unconstrained.
 */
export const allowedConditionsFor = (
  rowCondition: string,
  accepted: string[] | undefined
): string[] => {
  const acceptedSet =
    accepted && accepted.length > 0 ? accepted : [...DECLARABLE_CONDITIONS, rowCondition];
  const allowed: string[] = DECLARABLE_CONDITIONS.filter((c) => acceptedSet.includes(c));
  if (acceptedSet.includes(rowCondition) && !allowed.includes(rowCondition)) {
    allowed.unshift(rowCondition);
  }
  return allowed;
};

/**
 * Live accepted-condition union per have row, from the candidates index: a row
 * matching several want records may be offered at any condition ANY of them
 * accepts — the server's accept-if-any rule (Eric's 2026-07-30 ruling keeps it
 * permanently). Groups predating the explicit-list payloads contribute all
 * four declarable conditions (absent list = unconstrained, matching the
 * matcher). The builder's dropdown prefers this LIVE union over the set frozen
 * onto the item at add time, so a persisted or stale item can never widen (or
 * wrongly narrow) the dropdown beyond what the recipient's current want
 * records accept.
 */
export const buildAcceptedConditionsByHaveRow = (
  groups: ReadonlyArray<{
    wantEntry: { acceptedConditions?: string[] };
    matches: Array<{ userMTGCardHaveId: number }>;
  }>
): Map<number, string[]> => {
  const unions = new Map<number, Set<string>>();
  for (const group of groups) {
    const accepted: string[] =
      group.wantEntry.acceptedConditions && group.wantEntry.acceptedConditions.length > 0
        ? group.wantEntry.acceptedConditions
        : [...DECLARABLE_CONDITIONS];
    for (const row of group.matches) {
      let set = unions.get(row.userMTGCardHaveId);
      if (!set) {
        set = new Set<string>();
        unions.set(row.userMTGCardHaveId, set);
      }
      accepted.forEach((c) => set!.add(c));
    }
  }
  const result = new Map<number, string[]>();
  unions.forEach((set, haveRowId) => {
    result.set(haveRowId, Array.from(set));
  });
  return result;
};

/** Quoted price for declaring `condition` on this row; null = no price data */
export const priceForCondition = (
  row: {
    condition: string;
    quotedPricePoints: number | null;
    quotedPricesByCondition?: Record<string, number | null>;
  },
  condition: string
): number | null => {
  const fromMap = row.quotedPricesByCondition?.[condition];
  if (fromMap != null) return fromMap;
  // The row condition's entry equals quotedPricePoints; keep the fallback for
  // payloads predating the per-condition map.
  if (condition === row.condition) return row.quotedPricePoints;
  return null;
};

/**
 * Default declaration: the have row's condition when the recipient accepts it
 * (decision 8 — untouched selector reproduces today's behavior exactly).
 * When the want refuses the row's condition (a row offerable only through
 * re-declaration), pick the accepted condition closest to the row's,
 * preferring the worse one on ties — never silently promise a better card.
 */
export const defaultDeclaredCondition = (
  rowCondition: string,
  allowed: string[]
): string | undefined => {
  if (allowed.includes(rowCondition)) return rowCondition;
  if (allowed.length === 0) return undefined;
  const rowRank = conditionRank(rowCondition);
  return [...allowed].sort((a, b) => {
    const da = Math.abs(conditionRank(a) - rowRank);
    const db = Math.abs(conditionRank(b) - rowRank);
    if (da !== db) return da - db;
    return conditionRank(b) - conditionRank(a); // worse first on ties
  })[0];
};
